import { Component, OnInit, inject } from '@angular/core';
import { timer } from 'rxjs';
import { SithsEIDService } from '../../../backend/api';
import { SithsEIDPollingService } from '../../../backend/datasource';
import {
  AuthenticationMethodType,
  AuthenticationTicket,
  HintCode,
  PollResponse,
  StatusCode
} from '../../../backend/model';
import { SithsAutoStartUtil, UserAgentUtil } from '../../util';
import { AuthenticationContextDatasourceService } from '../../../backend/datasource/authentication-context-datasource.service';
import { LoadingSpinnerComponent } from '../../../shared/component/loading-spinner/loading-spinner.component';
import { IDCardModule } from '@inera/ids-angular/components/card';
import { IDButtonModule } from '@inera/ids-angular/components/button';
import { IDLinkModule } from '@inera/ids-angular/components/link';
import { ThemeHandlerService } from '../../service/theme-handler.service';
import { NgTemplateOutlet } from '@angular/common';
import { ThemeType } from '../../../backend/model/theme-type';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'idp-siths-eid-authentication',
  imports: [LoadingSpinnerComponent, IDCardModule, IDButtonModule, IDLinkModule, NgTemplateOutlet],
  templateUrl: './siths-eid-authentication.component.html',
  styleUrls: ['./siths-eid-authentication.component.scss']
})
export class SithsEIDAuthenticationComponent implements OnInit {
  private authenticationContextDatasource = inject(AuthenticationContextDatasourceService);
  private sithsEIDPollingService = inject(SithsEIDPollingService);
  private sithsEIDService = inject(SithsEIDService);
  private themeService = inject(ThemeHandlerService);
  private route = inject(ActivatedRoute);

  public base64Prefix = 'data:image/jpeg;base64,';
  public ticket: AuthenticationTicket;
  public pending = false;
  public qrToken = null;
  public qrTokenB64 = null;
  public displayQrCode = true;

  public isSameDevice = false;
  public isAndroid = false;
  public isStarting = false;

  public primaryColor: string = '';
  public mainTheme: string = '';

  public themeType = ThemeType;
  public clientInfo;

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.authenticationContextDatasource.setContextData(params);
      this.isSameDevice =
        this.authenticationContextDatasource.getAuthMethodType() === AuthenticationMethodType.SITHS_EID_SAME_DEVICE;

      this.getTicket();

      this.themeService.getTheme().subscribe(theme => {
        this.mainTheme = theme.value.value.theme.mainTheme;
      });
      this.clientInfo = this.authenticationContextDatasource.getClientInfo();
      this.primaryColor = document.documentElement.style.getPropertyValue('--primary-color');
    });

    this.isAndroid = UserAgentUtil.isAndroid();
  }

  private getTicket(): void {
    this.sithsEIDService
      .authenticationRequest(
        this.authenticationContextDatasource.getAuthMethodType(),
        this.authenticationContextDatasource.getSessionId(),
        this.authenticationContextDatasource.getIdp()
      )
      .subscribe(ticket => {
        this.ticket = ticket;
        this.sithsEIDService.setTicket(this.ticket);

        if (ticket.qrStartTokenCodeB64) {
          this.qrTokenB64 = ticket.qrStartTokenCodeB64;
        } else {
          this.qrTokenB64 = ticket.autoStartTokenQrCodeB64;
          this.qrToken = ticket.autoStartToken;
        }

        this.startPoll();

        if (!this.isAndroid || (this.isAndroid && !this.authenticationContextDatasource.getIsSingleMethod())) {
          this.autoStart(false);
        }
      });
  }

  private autoStart(manualAutoStart: boolean): void {
    if (this.isSameDevice || manualAutoStart) {
      this.tryStart();
    }
  }

  private tryStart(): void {
    if (this.isStarting === true) {
      return;
    }

    this.isStarting = true;

    timer(3000).subscribe(() => {
      this.isStarting = false;
    });

    SithsAutoStartUtil.autoStart(this.ticket);
  }

  private startPoll(): void {
    this.sithsEIDPollingService
      .connect(
        this.authenticationContextDatasource.getSessionId(),
        this.isSameDevice,
        this.authenticationContextDatasource.getIdp()
      )
      .subscribe(response => {
        if (this.evaluateIfStartedOrCompleted(response)) {
          if (response?.redirectUrl?.length) {
            window.location.href = response.redirectUrl;
          } else {
            this.pending = true;
          }
        } else {
          if (response?.qrStartTokenCodeB64?.length) {
            this.qrTokenB64 = response.qrStartTokenCodeB64;
          }
        }
      });
  }

  public manualAutostart(): void {
    this.displayQrCode = false;
    this.autoStart(true);
  }

  public cancelAuthentication(): void {
    this.sithsEIDService.cancelAuthentication(
      this.authenticationContextDatasource.getSessionId(),
      this.authenticationContextDatasource.getIdp()
    );
  }

  private evaluateIfStartedOrCompleted(data: PollResponse): boolean {
    return (
      data !== null &&
      ((data.statusCode === StatusCode.PENDING && data.hintCode === HintCode.PENDING_STARTED) ||
        data.statusCode === StatusCode.COMPLETE)
    );
  }

  public getTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'Jag signerar hos' : 'Jag legitimerar mig hos';
  }

  public getMethodTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'underskriften' : 'inloggningen';
  }

  public isSingleMethod(): boolean {
    return this.authenticationContextDatasource.getIsSingleMethod();
  }
}
